import React, { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "../../../Icons";
import { LIBRARIES_BTN } from "../librariesTheme";

/**
 * Bulk Action menu — single "Action" button with Activate / Deactivate.
 * Shown when rows are selected.
 */
const LibraryBulkActionMenu = ({
  selectedCount = 0,
  disabled = false,
  onActivate,
  onDeactivate,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (selectedCount === 0) setOpen(false);
  }, [selectedCount]);

  if (selectedCount < 1) return null;

  const pick = async (fn) => {
    setOpen(false);
    await fn?.();
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${LIBRARIES_BTN.outline} min-w-[7.5rem]`}
      >
        Action
        <span className="text-indigo-500/80 tabular-nums text-xs font-semibold">
          ({selectedCount})
        </span>
        <ChevronDownIcon
          size={14}
          className={`shrink-0 text-indigo-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-44 rounded-md border border-indigo-200 bg-white py-1 shadow-lg"
        >
          <button
            type="button"
            role="menuitem"
            disabled={disabled}
            onClick={() => pick(onActivate)}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-800 disabled:opacity-50"
          >
            Activate
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={disabled}
            onClick={() => pick(onDeactivate)}
            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-800 disabled:opacity-50"
          >
            Deactivate
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default LibraryBulkActionMenu;
